import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Flame, CalendarCheck, Trophy } from "lucide-react";

/**
 * Attendance streak + monthly class count for one member's profile.
 * A streak = consecutive weeks with at least one class (a dojo trains 2–3x a
 * week, so day streaks would always read zero).
 */
export default function AttendanceStats({ userId, compact = false }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const q = userId ? `?user_id=${userId}` : "";
    api.get(`/attendance/stats${q}`).then((r) => setS(r.data)).catch(() => setErr(true));
  }, [userId]);

  if (err) return null;

  const max = Math.max(1, ...(s?.monthly || []).map((m) => m.count));
  const streak = s?.current_streak_weeks ?? 0;
  const thisMonth = s?.classes_this_month ?? 0;
  const lastMonth = s?.classes_last_month ?? 0;
  const delta = thisMonth - lastMonth;

  return (
    <section className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]" data-testid="attendance-stats">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--dojo-border)]">
        <h2 className="font-serif text-2xl">Training Record</h2>
        <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">
          {s?.last_visit
            ? s.days_since_last_visit === 0
              ? "Trained today"
              : `Last class ${s.days_since_last_visit} day${s.days_since_last_visit === 1 ? "" : "s"} ago`
            : "No classes yet"}
        </span>
      </div>

      <div className="grid grid-cols-3 divide-x divide-[var(--dojo-border)]">
        <Tile
          icon={<Flame size={14} className={streak > 0 ? "text-[var(--dojo-hinomaru)]" : "text-[var(--dojo-ink-soft)]"} />}
          label="Current streak"
          value={streak}
          unit={streak === 1 ? "week" : "weeks"}
          sub={streak > 0 ? "Consecutive weeks training" : "Train this week to start one"}
          testid="stat-streak"
        />
        <Tile
          icon={<CalendarCheck size={14} className="text-[var(--dojo-green)]" />}
          label="This month"
          value={thisMonth}
          unit={thisMonth === 1 ? "class" : "classes"}
          sub={
            lastMonth === 0
              ? "First month on record"
              : `${delta >= 0 ? "+" : ""}${delta} vs last month (${lastMonth})`
          }
          testid="stat-this-month"
        />
        <Tile
          icon={<Trophy size={14} className="text-[var(--dojo-ink-soft)]" />}
          label="Best streak"
          value={s?.longest_streak_weeks ?? 0}
          unit="weeks"
          sub={`${s?.total_classes ?? 0} classes all-time`}
          testid="stat-best-streak"
        />
      </div>

      {!compact && (
        <div className="px-6 py-6 border-t border-[var(--dojo-border)]">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-4">Last six months</div>
          <div className="flex items-end gap-3 h-28" data-testid="attendance-monthly-chart">
            {(s?.monthly || []).map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-2" data-testid={`month-bar-${m.month}`}>
                <div className="font-mono-accent text-[11px] text-[var(--dojo-ink-soft)]">{m.count || ""}</div>
                <div
                  className="w-full bg-[var(--dojo-green)] transition-all duration-500"
                  style={{ height: `${Math.max(m.count ? 6 : 2, (m.count / max) * 76)}px`, opacity: m.count ? 1 : 0.18 }}
                />
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--dojo-ink-soft)]">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Tile({ icon, label, value, unit, sub, testid }) {
  return (
    <div className="px-6 py-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] mb-2">
        {icon} {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-4xl tracking-tight">{value}</span>
        <span className="text-xs text-[var(--dojo-ink-soft)]">{unit}</span>
      </div>
      <div className="text-xs text-[var(--dojo-ink-soft)] mt-1">{sub}</div>
    </div>
  );
}
